import client from './client';
import { Notification, PaginatedResponse, ListParams } from '../types/index';

// Bug #322: this module had two independent defects, either of which alone
// stopped the notification bell from ever clearing.
//
// 1. The write URLs did not exist. This module called
//    `PATCH /notifications/{id}` and `POST /notifications/mark-all-read`, but
//    the router registers `PATCH /notifications/{id}/read` and
//    `POST /notifications/read-all` (backend/app/routers/notifications.py:74,
//    107). Both calls 404'd. `useNotifications` catches and console.errors the
//    rejection, so the failure was silent and the local state update never ran.
// 2. The backend serialises snake_case (`is_read`, `created_at`, `source_app`,
//    `link_url`) — there is no camelCase alias generator on the backend and no
//    converting response interceptor in api/client.ts. Passing the raw payload
//    through as the camelCase `Notification` type left `isRead` permanently
//    `undefined`, so `unreadCount` counted every notification as unread forever
//    (and the source-app badge and relative timestamp rendered blank). Same
//    class of defect as Bug #146 (TAP applications); mapped here the same way
//    api/users.ts does it.

interface RawNotification {
  id: string;
  user_id: string;
  source_app?: string | null;
  title: string;
  message?: string | null;
  link_url?: string | null;
  is_read?: boolean | null;
  is_archived?: boolean | null;
  created_at?: string | null;
  read_at?: string | null;
}

const mapNotification = (n: RawNotification): Notification => ({
  id: n.id,
  sourceApp: n.source_app ?? '',
  title: n.title,
  message: n.message ?? '',
  linkUrl: n.link_url ?? undefined,
  isRead: Boolean(n.is_read),
  createdAt: n.created_at ?? '',
  readAt: n.read_at ?? undefined,
});

interface RawPaginatedNotifications {
  items: RawNotification[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// Query params are snake_case too — PaginationParams in schemas/common.py binds
// `page_size` / `sort_by` / `sort_order`. The camelCase `pageSize` this module
// used to send was ignored, silently capping every request at the default 20.
const toQueryParams = (params?: ListParams): Record<string, unknown> | undefined => {
  if (!params) return undefined;

  const { page, pageSize, sortBy, sortOrder, ...rest } = params;

  return {
    ...rest,
    ...(page !== undefined ? { page } : {}),
    ...(pageSize !== undefined ? { page_size: pageSize } : {}),
    ...(sortBy !== undefined ? { sort_by: sortBy } : {}),
    ...(sortOrder !== undefined ? { sort_order: sortOrder } : {}),
  };
};

export const getNotifications = async (params?: ListParams): Promise<PaginatedResponse<Notification>> => {
  const response = await client.get<RawPaginatedNotifications>('/notifications', {
    params: toQueryParams(params),
  });
  const data = response.data;

  return {
    items: (data.items || []).map(mapNotification),
    total: data.total,
    page: data.page,
    pageSize: data.page_size,
    pages: data.total_pages,
  };
};

// The endpoint takes no request body — the path itself is the intent.
export const markNotificationRead = async (id: string): Promise<Notification> => {
  const response = await client.patch<RawNotification>(`/notifications/${id}/read`);
  return mapNotification(response.data);
};

// Returns 204 No Content — there is no body, and no count, to read.
export const markAllNotificationsRead = async (): Promise<void> => {
  await client.post('/notifications/read-all');
};

// Admin-only endpoint (require_platform_admin). Currently unused by the SPA —
// notifications are created server-side by the automation dispatcher — but kept
// here for parity with the router. Request body is snake_case to match
// NotificationCreate.
export interface CreateNotificationInput {
  userId: string;
  sourceApp: string;
  title: string;
  message?: string;
  linkUrl?: string;
}

export const createNotification = async (data: CreateNotificationInput): Promise<Notification> => {
  const response = await client.post<RawNotification>('/notifications', {
    user_id: data.userId,
    source_app: data.sourceApp,
    title: data.title,
    message: data.message ?? null,
    link_url: data.linkUrl ?? null,
  });
  return mapNotification(response.data);
};
